import type { ReactNode } from 'react';
import { makeStyles, mergeClasses } from '@fluentui/react-components';
import type { Person } from '@vigeo/shared';
import { ShellIcons, shellMedia, tokens } from '@vigeo/ui';
import { placeWords } from '@/features/places';
import { placesOf, serviceAreaOf } from '@/features/roster';
import { NOT_RECORDED } from './profile.js';

/** The profile's shared look (Figma `1097:312`, `1097:1222`, `1096:804`): cards, label-and-value rows, place chips, quiet links. */
export const useProfileStyles = makeStyles({
  card: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, minWidth: 0,
    paddingBlock: tokens.spacingVerticalXL, paddingInline: tokens.spacingHorizontalXXL,
    backgroundColor: tokens.colorNeutralBackground1, borderRadius: tokens.borderRadiusXLarge,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
  },
  cardEditing: { border: `${tokens.strokeWidthThick} solid ${tokens.colorBrandBackground}` },
  cardHead: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, flexWrap: 'wrap' },
  cardTitle: { margin: 0, flexGrow: 1, fontSize: tokens.fontSizeBase400, lineHeight: tokens.lineHeightBase400, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1 },
  quiet: { color: tokens.colorNeutralForeground3 },
  note: { color: tokens.colorNeutralForeground2, fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200 },
  rows: { display: 'flex', flexDirection: 'column', margin: 0 },
  row: {
    display: 'flex', alignItems: 'flex-start', gap: tokens.spacingHorizontalM, paddingBlock: tokens.spacingVerticalS,
    borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    ':first-child': { borderTop: 'none' },
    fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300,
    [shellMedia.phone]: { flexDirection: 'column', gap: tokens.spacingVerticalXXS },
  },
  label: { flexShrink: 0, width: '6.5rem', margin: 0, color: tokens.colorNeutralForeground2 },
  labelWide: { width: '10rem' },
  value: { flexGrow: 1, minWidth: 0, margin: 0, color: tokens.colorNeutralForeground1, overflowWrap: 'anywhere' },
  empty: { color: tokens.colorNeutralForeground3 },
  chips: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalXS },
  chip: {
    display: 'inline-block', maxWidth: '100%', paddingBlock: tokens.spacingVerticalXXS, paddingInline: tokens.spacingHorizontalS,
    backgroundColor: tokens.colorNeutralBackground2, border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusSmall, fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300, overflowWrap: 'anywhere',
  },
  initials: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, width: '3.5rem', height: '3.5rem',
    borderRadius: tokens.borderRadiusCircular, backgroundColor: tokens.colorBrandBackground2, color: tokens.colorBrandForeground1,
    fontSize: tokens.fontSizeBase500, lineHeight: tokens.lineHeightBase500, fontWeight: tokens.fontWeightSemibold,
  },
  initialsSmall: { width: '3rem', height: '3rem', fontSize: tokens.fontSizeBase400, lineHeight: tokens.lineHeightBase400 },
  go: {
    display: 'inline-flex', alignItems: 'center', gap: tokens.spacingHorizontalXS, alignSelf: 'flex-start',
    padding: 0, border: 'none', backgroundColor: 'transparent', cursor: 'pointer', textDecorationLine: 'none',
    color: tokens.colorBrandForeground1, fontFamily: 'inherit', fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300, fontWeight: tokens.fontWeightSemibold,
    ':hover': { textDecorationLine: 'underline' },
    [shellMedia.phone]: { minHeight: '2.75rem' },
  },
});

/** The person's initials in a tinted circle (Figma `1097:521`). Decoration: the name is written beside it. */
export function Initials({ person, small = false }: { readonly person: Person; readonly small?: boolean }) {
  const styles = useProfileStyles();
  const letters = `${(person.name.preferredName || person.name.givenName).charAt(0)}${person.name.familyName.charAt(0)}`.toUpperCase();
  return <span aria-hidden="true" className={mergeClasses(styles.initials, small && styles.initialsSmall)}>{letters}</span>;
}

export interface Fact { readonly label: string; readonly value: ReactNode }

/** Label on the left, value on the right, a hairline between rows. An empty value reads "Not recorded", quietly. */
export function FactRows({ facts, wide = false, 'aria-label': ariaLabel }: { readonly facts: readonly Fact[]; readonly wide?: boolean; readonly 'aria-label'?: string }) {
  const styles = useProfileStyles();
  return (
    <dl className={styles.rows} aria-label={ariaLabel}>
      {facts.map((fact) => {
        const blank = fact.value === null || fact.value === undefined || fact.value === '';
        return (
          <div key={fact.label} className={styles.row}>
            <dt className={mergeClasses(styles.label, wide && styles.labelWide)}>{fact.label}</dt>
            <dd className={mergeClasses(styles.value, blank && styles.empty)}>{blank ? NOT_RECORDED : fact.value}</dd>
          </div>
        );
      })}
    </dl>
  );
}

/** A card with a title, something at the right of the title, and a body. */
export function Card({ title, aside, editing = false, children, 'aria-label': ariaLabel }: {
  readonly title: string; readonly aside?: ReactNode; readonly editing?: boolean; readonly children: ReactNode; readonly 'aria-label'?: string;
}) {
  const styles = useProfileStyles();
  return (
    <section className={mergeClasses(styles.card, editing && styles.cardEditing)} aria-label={ariaLabel ?? title}>
      <div className={styles.cardHead}><h2 className={styles.cardTitle}>{title}</h2>{aside}</div>
      {children}
    </section>
  );
}

/** A quiet link with an arrow that moves within the profile: "All in Personal". A button, because it changes a tab, not the address bar's page. */
export function GoButton({ children, onClick }: { readonly children: ReactNode; readonly onClick: () => void }) {
  const styles = useProfileStyles();
  return <button type="button" className={styles.go} onClick={onClick}>{children}<ShellIcons.arrowRight /></button>;
}

/**
 * A person's service areas as chips: each picked place by its name in the app's own list of places (ADR-021, #594), or the
 * text typed before places existed (ADR-018 §1), or nothing. The same reading the Roster's own column gives.
 */
export function ServiceAreaChips({ person }: { readonly person: Person }) {
  const styles = useProfileStyles();
  const places = placesOf(person);
  if (places.length > 0) {
    return <span className={styles.chips}>{places.map((place) => <span key={place.placeId} className={styles.chip}>{placeWords(place.placeId)}</span>)}</span>;
  }
  const area = serviceAreaOf(person);
  return area === null ? <span className={styles.empty}>No service area</span> : <span className={styles.chip}>{area}</span>;
}

/** The same, as one line of words for the summary header: "Norfolk · Virginia Beach +1". */
export function serviceAreaWords(person: Person, shown = 2): string | null {
  const places = placesOf(person);
  if (places.length === 0) return serviceAreaOf(person);
  const words = places.slice(0, shown).map((place) => placeWords(place.placeId)).join(' · ');
  return places.length > shown ? `${words} +${places.length - shown}` : words;
}
